import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/Loader";
import { LogOut, BadgeCheck } from "lucide-react";

export default function ProfilePage() {
  const { logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/nurse/me").then((r) => {
      setData(r.data);
      setForm({
        name: r.data.name || "", phone: r.data.phone || "", qualification: r.data.qualification || "",
        experience_years: r.data.experience_years || "", city: r.data.city || "",
      });
    }).catch((e) => toast.error(apiError(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/nurse/profile", form);
      setData({ ...data, ...r.data });
      toast.success("Profile saved");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  if (!data) return <Loader />;
  const fields = [
    { k: "name", label: "Full name" },
    { k: "phone", label: "Phone" },
    { k: "qualification", label: "Qualification" },
    { k: "experience_years", label: "Years of experience" },
    { k: "city", label: "City" },
  ];

  return (
    <div className="space-y-5 pb-6" data-testid="nurse-profile">
      <h1 className="text-[24px] font-bold text-[#1c1b1b]">Profile</h1>

      {data.eligibility?.length > 0 && (
        <div className="bg-[#004e33]/10 border border-[#004e33]/20 rounded-lg p-4" data-testid="eligibility-card">
          <p className="text-sm font-semibold text-[#004e33] flex items-center gap-2 mb-2"><BadgeCheck className="w-4 h-4" /> Eligible Services</p>
          <div className="flex flex-wrap gap-2">
            {data.eligibility.map((s) => (
              <span key={s} className="text-xs font-semibold bg-white text-[#004e33] px-3 py-1 rounded-full border border-[#004e33]/20">{s}</span>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-[#E5E7EB] p-5 space-y-4">
        {fields.map((f) => (
          <div key={f.k}>
            <label className="text-sm font-semibold text-[#1c1b1b]">{f.label}</label>
            <input
              data-testid={`profile-${f.k}-input`}
              value={form[f.k] || ""}
              onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
              className="mt-1 h-12 w-full rounded-lg border border-[#E5E7EB] bg-white px-4 text-[16px] focus:outline-none focus:ring-2 focus:ring-[#003d9b]"
            />
          </div>
        ))}
        <button onClick={save} disabled={saving} data-testid="save-profile-btn"
          className="w-full h-12 rounded-lg bg-[#003d9b] text-white font-semibold hover:bg-[#0052cc] transition-colors disabled:opacity-50">
          {saving ? "Saving…" : "Save Profile"}
        </button>
      </div>

      <button onClick={doLogout} data-testid="nurse-logout-btn"
        className="w-full h-12 rounded-lg border border-[#E5E7EB] bg-white text-[#ba1a1a] font-semibold flex items-center justify-center gap-2 hover:bg-red-50 transition-colors">
        <LogOut className="w-4 h-4" /> Log Out
      </button>
    </div>
  );
}
